import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Avatar, Badge, Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchChangelog } from '../api/changelog';
import { openWhatsNew, WHATS_NEW_KEY } from './WhatsNewDialog';
import { useAuth } from '../services/AuthContext';
import { C, FONT, capsuleSx, glow, hexToRgba, navPillSx, prefersReducedMotion, zIndex } from '../theme/tokens';

interface NavItem {
  label: string;
  path: string;
}

// Past this much scroll the capsule tightens a notch — the same "the
// interface is paying attention" nod adventure's nav makes. Not a
// hide-on-scroll: this is the only nav, hiding it costs more than it earns.
const SCROLL_THRESHOLD = 24;

/**
 * The console's nav: a floating glass capsule, centered over the void.
 *
 * v12 built this as an LCARS bar — a full-width docked strip with an elbow,
 * a colour-blocked rail, and a per-section accent. That was the single
 * biggest source of the "too much LCARS" verdict, because chrome is what you
 * see on every screen. This is adventure's own treatment instead: a rounded
 * capsule with a blurred translucent fill and a wormhole-tinted edge,
 * carrying a soft cyan pill that slides between links.
 *
 * What survives from the brief, because it was asked for directly:
 *  - centered links,
 *  - the indicator *moves* rather than each item drawing its own underline,
 *  - it lights up on click — now a brief bloom on the pill rather than a
 *    stuttering LED bar, which belonged to the LCARS reading.
 */
const NavBar: React.FC = () => {
  const { me, login, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const listRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [strike, setStrike] = useState(0);

  // Same query the dialog itself makes (react-query dedupes it), just to
  // know whether the bell needs a dot — the dialog owns showing itself, this
  // only owns the reopen affordance and its "there's something new" mark.
  const { data: changelog } = useQuery({ queryKey: ['changelog'], queryFn: fetchChangelog, enabled: me.isMember, staleTime: Infinity });
  const latestVersion = changelog?.[0]?.version;
  const hasUnseenRelease = !!latestVersion && localStorage.getItem(WHATS_NEW_KEY) !== latestVersion;

  const links: NavItem[] = [
    { label: 'Dashboard', path: '/' },
    ...(me.isMember
      ? [
          { label: 'Intel', path: '/intel/current' },
          { label: 'Find', path: '/find' },
          { label: 'Data Vault', path: '/vault' },
          { label: 'Fittings', path: '/fittings' },
          { label: 'Handbook', path: '/handbook' },
          { label: 'Settings', path: '/settings' },
        ]
      : []),
    ...(me.isAdmin || me.isDirector ? [{ label: 'Admin', path: '/admin' }] : []),
  ];

  // Intel is the live page (/intel/current). /system/* lights it too: a fixed
  // system page is still an Intel page.
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/intel/current') return location.pathname.startsWith('/intel') || location.pathname.startsWith('/system');
    return location.pathname.startsWith(path);
  };
  const activePath = links.find((l) => isActive(l.path))?.path;

  /** Measured from the real elements, and keyed on `activePath` (a string)
   * rather than on the active object — `links` is rebuilt every render, so an
   * object dependency made this callback new every render, re-firing the
   * layout effect, setting state, rendering again: React error #185, blank
   * page. The setter also bails when the numbers are unchanged.
   *
   * offsetLeft/offsetWidth, not getBoundingClientRect: the capsule itself
   * animates `transform: scale()` on scroll, and rect-based measurements taken
   * mid-transition land the pill in the wrong place. */
  const measure = useCallback(() => {
    const el = activePath ? itemRefs.current.get(activePath) : undefined;
    if (!el) {
      setIndicator((prev) => (prev === null ? prev : null));
      return;
    }
    const next = { left: el.offsetLeft, width: el.offsetWidth };
    setIndicator((prev) => (prev && prev.left === next.left && prev.width === next.width ? prev : next));
  }, [activePath]);

  useLayoutEffect(measure, [measure, location.pathname, me.isMember, me.isAdmin, me.isDirector]);

  // On a narrow screen the active link can sit outside the scrolled view;
  // bring it into range so you can always see where you are.
  useEffect(() => {
    if (!activePath) return;
    itemRefs.current.get(activePath)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activePath]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    // The pill is positioned against text laid out in a webfont; measuring
    // before Rajdhani lands leaves it a few px off forever.
    void document.fonts?.ready.then(measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setScrolled(window.scrollY > SCROLL_THRESHOLD);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: zIndex.navbar,
        display: 'flex',
        justifyContent: 'center',
        px: { xs: 1.5, md: 3 },
        pt: { xs: 1.5, md: 2 },
        pb: { xs: 1, md: 1.25 },
        // The gutter stays click-through — only the capsule intercepts
        // pointer events, or the header silently eats clicks behind it.
        pointerEvents: 'none',
      }}
    >
      <Box
        component="nav"
        aria-label="Main"
        sx={{
          pointerEvents: 'auto',
          width: '100%',
          maxWidth: 1180,
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.25, md: 2.5 },
          px: { xs: 1.75, md: 3 },
          py: 1.1,
          ...capsuleSx(C.wormhole, scrolled),
          // Condense via transform, not padding — a layout property would
          // thrash layout every frame; scale is composite-only.
          transform: scrolled ? 'scale(0.97)' : 'scale(1)',
          transformOrigin: 'top center',
          transition: prefersReducedMotion()
            ? 'none'
            : 'transform 0.35s cubic-bezier(0.16,1,0.3,1), border-color 0.35s ease, box-shadow 0.35s ease',
        }}
      >
        <Typography
          noWrap
          onClick={() => navigate('/')}
          sx={{
            display: { xs: 'none', md: 'block' },
            fontFamily: FONT.display,
            fontWeight: 700,
            fontSize: '0.92rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: C.text,
            cursor: 'pointer',
            textShadow: glow(C.wormhole, 14, 0.5),
            flexShrink: 0,
          }}
        >
          {/* Rendered WH-GOBBO by the uppercase transform above — the
              wordmark has always been all-caps. The text itself carries the
              real casing so the accessible name and the tab reading stay
              "WH-Gobbo" rather than a shout. */}
          WH-Gobbo
        </Typography>

        <Box
          ref={listRef}
          sx={{
            position: 'relative',
            display: 'flex',
            gap: 0.25,
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            // Centred once the links fit; left-anchored on narrow screens,
            // where centring a scroll container starts it mid-list with the
            // first items (Dashboard, Intel) hidden off to the left.
            justifyContent: { xs: 'flex-start', md: 'center' },
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {indicator && (
            <Box
              aria-hidden
              key={strike}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: indicator.width,
                transform: `translateX(${indicator.left}px)`,
                ...navPillSx(C.cyan),
                pointerEvents: 'none',
                transition: prefersReducedMotion()
                  ? 'none'
                  : 'transform 0.4s cubic-bezier(0.16,1,0.3,1), width 0.4s cubic-bezier(0.16,1,0.3,1)',
                // Re-keyed on each click so the bloom replays: the light
                // under the button the brief asked for, in this world's
                // vocabulary (a glow swell) rather than LCARS's LED stutter.
                animation: prefersReducedMotion() || strike === 0 ? 'none' : 'navStrike 0.5s ease-out 1',
                '@keyframes navStrike': {
                  '0%': { boxShadow: `0 0 0 ${hexToRgba(C.cyan, 0)} inset, 0 0 26px ${hexToRgba(C.cyan, 0.75)}` },
                  '100%': {
                    boxShadow: `0 0 12px ${hexToRgba(C.cyan, 0.3)} inset, 0 0 10px ${hexToRgba(C.cyan, 0.18)}`,
                  },
                },
              }}
            />
          )}

          {links.map((item) => {
            const on = isActive(item.path);
            return (
              <Box
                key={item.path}
                component="button"
                type="button"
                aria-current={on ? 'page' : undefined}
                ref={(el: HTMLElement | null) => {
                  if (el) itemRefs.current.set(item.path, el);
                  else itemRefs.current.delete(item.path);
                }}
                onClick={() => {
                  setStrike((n) => n + 1);
                  navigate(item.path);
                }}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  appearance: 'none',
                  background: 'none',
                  border: 'none',
                  px: { xs: 1.25, md: 1.75 },
                  py: 0.5,
                  borderRadius: 999,
                  cursor: 'pointer',
                  flexShrink: 0,
                  fontFamily: FONT.sub,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                  color: on ? C.textBright : C.textDim,
                  transition: 'color 0.25s ease',
                  '&:hover': { color: C.text },
                  '&:focus-visible': { outline: `2px solid ${C.cyan}`, outlineOffset: '2px' },
                }}
              >
                {item.label}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {me.authenticated && me.character ? (
            <>
              {me.isMember && (
                <Tooltip title="What's new">
                  <IconButton
                    size="small"
                    onClick={() => openWhatsNew()}
                    sx={{ color: hasUnseenRelease ? C.gold : C.textDim, '&:hover': { color: C.gold } }}
                  >
                    <Badge variant="dot" color="warning" invisible={!hasUnseenRelease}>
                      <NewReleasesIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
              <Avatar
                src={`https://images.evetech.net/characters/${me.character.id}/portrait?size=64`}
                sx={{ width: 30, height: 30, border: `1px solid ${hexToRgba(C.wormhole, 0.45)}` }}
              />
              <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0, maxWidth: 150 }}>
                <Typography noWrap sx={{ color: C.text, fontFamily: FONT.sub, fontWeight: 600, fontSize: '0.86rem', lineHeight: 1.2 }}>
                  {me.character.name}
                </Typography>
                <Typography noWrap sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.64rem' }}>
                  {me.character.corpName ?? '—'}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => logout()}
                sx={{ color: C.textDim, minWidth: 0, fontFamily: FONT.sub, fontSize: '0.78rem', '&:hover': { color: C.text } }}
              >
                Log out
              </Button>
            </>
          ) : (
            <Button
              size="small"
              variant="outlined"
              onClick={() => login()}
              sx={{
                color: C.wormhole,
                borderColor: hexToRgba(C.wormhole, 0.5),
                px: 2,
                fontFamily: FONT.sub,
                fontWeight: 600,
                letterSpacing: '0.06em',
                fontSize: '0.8rem',
                '&:hover': { borderColor: C.wormhole, boxShadow: glow(C.wormhole, 18, 0.35) },
              }}
            >
              Log in with EVE
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NavBar;
